import { ThemeProvider as ChakraProvider } from "@chakra-ui/core";
import { config } from "@fortawesome/fontawesome-svg-core"; // https://github.com/FortAwesome/react-fontawesome#nextjs
import "@fortawesome/fontawesome-svg-core/styles.css";
import type ApolloClient from "apollo-client";
import { AppProvider } from "components/AppContext";
import { AppStoreReviewProvider } from "components/AppStoreReview";
import { useUserIdLazyWithClient } from "components/hooks";
import PersistedModalProvider from "components/PersistedModalContext";
import PushNotificationsProvider from "components/PushNotificationsContext";
import { useTrackSession } from "lib/analytics";
import withApollo from "lib/apollo/withApollo";
import { bindAppRouteWarning, ctxIsApp } from "lib/app";
import { bindCypressBridge } from "lib/cypress";
import { appInitialRouteCheck } from "lib/guards/appInitialRouteCheck";
import { watchUserIdentification } from "lib/guards/watchUserIdentification";
import { Sentry } from "lib/sentry";
import { chakraTheme, systemTheme } from "lib/theme";
import App from "next/app";
import Router, { useRouter } from "next/router";
import React from "react";
import { ApolloProvider } from "react-apollo";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import smoothscroll from "smoothscroll-polyfill";
import { ThemeProvider } from "styled-components";

config.autoAddCss = false; // Tell Font Awesome to skip adding the CSS automatically since it's being imported above

const clearIntro = () => {
  if (!localStorage || typeof localStorage !== "object") {
    return;
  }
  delete localStorage["intro"];
};

bindAppRouteWarning();
appInitialRouteCheck();
bindCypressBridge();

Router.events.on("routeChangeError", (err) => {
  if (process.env.NODE_ENV === "development") {
    console.error("Error changing route:");
    console.error(err);
  }
  if (!err.cancelled) {
    Sentry.captureException(err);
  }
});

if (process.browser) {
  smoothscroll.polyfill();
}

const scrollWindowToTop = () =>
  setTimeout(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  });

const ReCaptchaProvider = ({ children }) => {
  if (process.env.NEXT_PUBLIC_FEATURE_RECAPTCHA === "true") {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY_V3}
        useEnterprise
        scriptProps={{
          async: true, // optional, default to false,
          nonce: undefined, // optional, default undefined
        }}
      >
        {children}
      </GoogleReCaptchaProvider>
    );
  } else {
    return children;
  }
};

const MyAppRender = ({ apollo, Component, pageProps, err, isApp }) => {
  const router = useRouter();

  const userId = useUserIdLazyWithClient(apollo);
  useTrackSession(!!userId);

  return (
    <AppProvider isApp={isApp}>
      <ApolloProvider client={apollo}>
        <PushNotificationsProvider
          shouldRequestPermissions={userId && router.pathname === "/account"}
        >
          <ReCaptchaProvider>
            <ThemeProvider theme={{ ...systemTheme, isApp }}>
              <ChakraProvider theme={chakraTheme}>
                <PersistedModalProvider>
                  <AppStoreReviewProvider>
                    {/* err prop is a workaround for https://github.com/vercel/next.js/issues/8592 */}
                    <Component {...pageProps} err={err} />
                  </AppStoreReviewProvider>
                </PersistedModalProvider>
              </ChakraProvider>
            </ThemeProvider>
          </ReCaptchaProvider>
        </PushNotificationsProvider>
      </ApolloProvider>
    </AppProvider>
  );
};

interface AppProps {
  apollo: ApolloClient<any>;
  isAppRequest: boolean;
  err?: Error;
}

class MyApp extends App<AppProps> {
  componentDidMount() {
    Router.events.on("routeChangeComplete", () => scrollWindowToTop());

    watchUserIdentification(this.props.apollo);

    // At some point a users sign up data including their plain-text password
    // were saved into localstorage. Delete this data if it exists
    // This could be removed in the future.
    clearIntro();
  }

  static async getInitialProps({ ctx, Component }) {
    // Default behaviour
    const pageProps = Component.getInitialProps
      ? await Component.getInitialProps(ctx)
      : {};

    return {
      pageProps,
      isAppRequest: ctxIsApp(ctx),
    };
  }

  render() {
    const { Component, pageProps, apollo, isAppRequest, err } = this.props;

    // check a second time for cases where getInitialProps doesn't run (statically generated pages)
    const isApp = isAppRequest || ctxIsApp();

    return (
      <MyAppRender
        err={err}
        isApp={isApp}
        apollo={apollo}
        pageProps={pageProps}
        Component={Component}
      />
    );
  }
}

export default withApollo(MyApp);
